'use client';

import { createContext, useContext, ReactNode, useMemo, useEffect } from 'react';
import { useAppSelector } from '../store/hooks';
import { FontSizeOption } from '../../features/settings/store/settingsSlice';

interface FontSizeClasses {
  // Message-specific (existing)
  messageFontClass: string;
  
  // Text type classes (new - additive to existing system)
  bodyText: string;
  headingText: string; 
  labelText: string;
  captionText: string;
  
  // Size multiplier for custom implementations
  sizeMultiplier: number;
}

interface FontSizeContextType {
  fontSize: FontSizeOption;
  classes: FontSizeClasses;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export function FontSizeProvider({ children }: { children: ReactNode }) {
  const fontSize = useAppSelector(state => state.settings.messageFontSize);
  
  // Generate font size classes based on the selected option
  // PRESERVES existing messageFontClass for backward compatibility
  const classes = useMemo((): FontSizeClasses => {
    switch (fontSize) {
      case 'large':
        return {
          messageFontClass: 'text-lg', // UNCHANGED - preserves existing designs  
          bodyText: 'text-base',
          headingText: 'text-xl',
          labelText: 'text-sm', 
          captionText: 'text-sm',
          sizeMultiplier: 1.125
        };
      default: // medium
        return {
          messageFontClass: 'text-base', // UNCHANGED - preserves existing designs
          bodyText: 'text-sm',
          headingText: 'text-lg',
          labelText: 'text-xs',
          captionText: 'text-xs', 
          sizeMultiplier: 1
        };
    }
  }, [fontSize]);

  // Apply CSS custom properties for components that need fine-grained control
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-font-scale', classes.sizeMultiplier.toString());
  }, [classes.sizeMultiplier]);

  return (
    <FontSizeContext.Provider value={{ fontSize, classes }}>
      <div data-font-scaling="enabled">
        {children}
      </div>
    </FontSizeContext.Provider>
  );
}

export function useFontSize() {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  
  // BACKWARD COMPATIBILITY: Return messageFontClass directly for existing components
  return {
    fontSize: context.fontSize,
    messageFontClass: context.classes.messageFontClass, // Existing usage preserved
    classes: context.classes // New usage available
  };
} 